import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import { RiCheckLine, RiLock2Fill, RiSubtractLine } from 'react-icons/ri';

import { mergeClasses } from '@utils';

import {
  formatPrizeDay,
  formatRelativeDay,
  formatUpcomingDay,
  getPrizeState
} from '../utils';

export const chipClassName =
  'inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 text-xs font-semibold uppercase leading-none tracking-wide text-black';

/**
 * A chip naming the prize's state: today, joined, expired or the day it opens.
 * `compact` reduces it to its icon from the `lg` breakpoint, and at every size
 * when its label would only repeat the prize's day.
 */
export default function PrizeStatus({ attributes, className, compact }) {
  const state = getPrizeState(attributes);

  const label = {
    today: formatRelativeDay(0),
    joined: t`Joined`,
    expired: t`Expired`,
    upcoming: formatUpcomingDay(attributes.valid_from)
  }[state];

  const isIconOnly =
    compact &&
    state === 'upcoming' &&
    label === formatPrizeDay(attributes.valid_from);

  const icon = {
    today: <span className="h-2 w-2 rounded-full bg-black" />,
    joined: <RiCheckLine className="h-3.5 w-3.5 text-green-600" />,
    expired: <RiSubtractLine className="h-3.5 w-3.5" />,
    upcoming: <RiLock2Fill className="h-3.5 w-3.5" />
  }[state];

  return (
    <span
      className={mergeClasses(
        chipClassName,
        {
          'bg-gradient-to-r from-green to-[#E0F65A]': state === 'today',
          'text-dark-gray': state === 'expired',
          'lg:h-7 lg:w-7 lg:justify-center lg:px-0': compact,
          'w-6 justify-center px-0': isIconOnly
        },
        className
      )}
    >
      {icon}
      <span
        className={mergeClasses({
          'lg:sr-only': compact,
          'sr-only': isIconOnly
        })}
      >
        {label}
      </span>
    </span>
  );
}

PrizeStatus.propTypes = {
  attributes: PropTypes.shape({
    status: PropTypes.string.isRequired,
    is_participating: PropTypes.bool,
    valid_from: PropTypes.string.isRequired
  }).isRequired,
  className: PropTypes.string,
  compact: PropTypes.bool
};

PrizeStatus.defaultProps = {
  className: '',
  compact: false
};
